import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./targetFunnelConfiguration.css";
import Edit from "../../../../../Images/Edit.png";
import Delete from "../../../../../Images/deleteButton-icon.png";
import { Get_LeadIndicatorFunnel, LeadFunnel } from "../../../../../services/userAPI";
import { useMutation, useQuery } from "@apollo/client";
import { message } from "antd";
import isEqual from "lodash.isequal";
import { DownOutlined, UpOutlined } from "@ant-design/icons";


const LeadIndicatorConfiguration = ({ onIndicatorsChange, roleId, accountId }) => {
    const navigate = useNavigate();
    const [isLeadCollapsed, setIsLeadCollapsed] = useState(false);
    const [isAddingNewIndicator, setIsAddingNewIndicator] = useState(false);
    const [indicators, setIndicators] = useState([]);
    const [editingIndex, setEditingIndex] = useState(null);

    const [indicatorName, setIndicatorName] = useState("");
    const [dailyNorm, setDailyNorm] = useState("");
    const [indicatorTarget, setIndicatorTarget] = useState("");
    const [color, setColor] = useState("#add8e6");
    const [errors, setErrors] = useState({});

    const location = useLocation();
    const finalRoleId = roleId || location.state?.roleId;
    const finalAccountId = accountId || location.state?.accountId;

    // Fetch existing lead indicators
    const { data, refetch } = useQuery(Get_LeadIndicatorFunnel, {
        variables: { roleId: finalRoleId },
        skip: !finalRoleId,
    });

    const formatColorForDisplay = (colorValue) => {
        if (!colorValue) return "#add8e6";
        let colorCode = colorValue;
        if (colorCode.toLowerCase().startsWith("ff")) {
            colorCode = colorCode.slice(2);
        }
        if (!colorCode.startsWith("#")) {
            colorCode = `#${colorCode}`;
        }
        return colorCode;
    };

    const formatColorForAPI = (colorValue) => {
        if (!colorValue) return "add8e6";
        return colorValue.startsWith("#") ? colorValue.slice(1) : colorValue;
    };

    useEffect(() => {
        if (data?.getKiisForRole) {
            // Sort indicators by id in ascending order to show newest at the end
            const updatedIndicators = data.getKiisForRole
                .map(kpi => ({
                    id: kpi.id,
                    name: kpi.name,
                    dailyNorm: kpi.dailyTarget,
                    target: kpi.defaultValue,
                    color: formatColorForDisplay(kpi.colorValue),
                }))
                .sort((a, b) => a.id - b.id); // Sort by id ascending

            if (!isEqual(updatedIndicators, indicators)) {
                setIndicators(updatedIndicators);
                if (onIndicatorsChange) {
                    onIndicatorsChange(updatedIndicators);
                }
            }
        }
    }, [data, onIndicatorsChange]);

    const validate = () => {
        let newErrors = {};
        if (!indicatorName.trim()) newErrors.indicatorName = "Lead Indicator Name is required.";
        if (!dailyNorm || isNaN(dailyNorm) || Number(dailyNorm) <= 0) {
            newErrors.dailyNorm = "Daily Norm must be a positive number.";
        }
        if (!indicatorTarget || isNaN(indicatorTarget) || Number(indicatorTarget) <= 0) {
            newErrors.indicatorTarget = "Monthly Target must be a positive number.";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const [saveKiiMasterDetails, { loading }] = useMutation(LeadFunnel);

    const resetForm = () => {
        setIndicatorName("");
        setDailyNorm("");
        setIndicatorTarget("");
        setColor("#add8e6");
        setIsAddingNewIndicator(false);
        setEditingIndex(null);
    };

    const handleCancel = () => {
        resetForm();
    };

    const handleAddOrUpdate = async () => {
        if (validate()) {
            try {
                const isEditing = editingIndex !== null;
                const selectedIndicator = isEditing ? indicators[editingIndex] : null;

                const brandColor = formatColorForAPI(color);

                const response = await saveKiiMasterDetails({
                    variables: {
                        accountId: finalAccountId,
                        roleId: finalRoleId,
                        kiiId: isEditing ? selectedIndicator.id : null,
                        kiiName: indicatorName,
                        kiiDailyTarget: Number(dailyNorm),
                        kiiDefaultTarget: Number(indicatorTarget),
                        kiiColorCode: brandColor,
                    },
                });

                if (response.data.saveKiiMasterDetails.success) {
                    await refetch();
                    const newIndicator = {
                        id: response.data.saveKiiMasterDetails.data.id,
                        name: response.data.saveKiiMasterDetails.data.name,
                        dailyNorm: response.data.saveKiiMasterDetails.data.dailyTarget,
                        target: response.data.saveKiiMasterDetails.data.defaultValue,
                        color: formatColorForDisplay(response.data.saveKiiMasterDetails.data.colorValue),
                    };

                    let updatedIndicators;
                    if (isEditing) {
                        updatedIndicators = indicators.map((indicator, index) =>
                            index === editingIndex ? newIndicator : indicator
                        );
                    } else {
                        // Add new indicator to the end
                        updatedIndicators = [...indicators, newIndicator];
                    }

                    // Sort by id ascending to maintain consistent order
                    updatedIndicators = updatedIndicators.sort((a, b) => a.id - b.id);

                    setIndicators(updatedIndicators);
                    if (onIndicatorsChange) {
                        onIndicatorsChange(updatedIndicators);
                    }

                    resetForm();
                } else {
                    message.error(response.data.saveKiiMasterDetails.message || "Failed to save lead indicator.");
                }
            } catch (err) {
                console.error("API Error:", err);
            }
        }
    };

    const handleEdit = (index) => {
        const indicator = indicators[index];
        setIndicatorName(indicator.name);
        setDailyNorm(indicator.dailyNorm);
        setIndicatorTarget(indicator.target);
        setColor(indicator.color);
        setEditingIndex(index);
        setIsAddingNewIndicator(true);
    };

    const handleDelete = async (index) => {
        const indicatorToDelete = indicators[index];

        try {
            const colorCode = formatColorForAPI(indicatorToDelete.color);

            const response = await saveKiiMasterDetails({
                variables: {
                    accountId: finalAccountId,
                    roleId: finalRoleId,
                    kiiId: indicatorToDelete.id,
                    kiiName: indicatorToDelete.name,
                    kiiDailyTarget: indicatorToDelete.dailyNorm,
                    kiiDefaultTarget: indicatorToDelete.target,
                    kiiColorCode: colorCode,
                    status: 0,
                },
            });

            if (response.data.saveKiiMasterDetails.success) {
                await refetch();
                const updatedIndicators = indicators.filter((_, i) => i !== index);
                setIndicators(updatedIndicators.sort((a, b) => a.id - b.id));
                if (onIndicatorsChange) {
                    onIndicatorsChange(updatedIndicators);
                }
            } else {
                message.error(response.data.saveKiiMasterDetails.message || "Failed to delete lead indicator.");
            }
        } catch (err) {
            console.error("API Error:", err);
        }
    };

    const [tooltipText, setTooltipText] = useState("");
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

    const handleMouseEnter = (e, text) => {
        setTooltipText(text);
        setTooltipPosition({ x: e.pageX, y: e.pageY });
        setShowTooltip(true);
    };
    const handleMouseLeave = () => {
        setShowTooltip(false);
    };

    return (
        <div>
            <div className="accordion">
                <div className="accordion-item">
                    <button className="accordion-header" onClick={() => setIsLeadCollapsed(!isLeadCollapsed)}>
                        <span>Lead Indicator Configuration</span>
                        <span className="text-black text-[12px] font-normal leading-none">
                            {isLeadCollapsed ? <DownOutlined /> : <UpOutlined />}
                        </span>                   </button>

                    {!isLeadCollapsed && (
                        <div className="accordion-content">
                            <div className="horizontal-scroll-container">
                                <div className="target-container-horizontal">
                                    {indicators.map((indicator, index) =>
                                        editingIndex === index ? (
                                            <div key={indicator.id} className="target-box">
                                                <input
                                                    type="text"
                                                    placeholder="Lead Indicator Name"
                                                    className="target-input"
                                                    value={indicatorName}
                                                    onChange={(e) => setIndicatorName(e.target.value)}
                                                />
                                                {errors.indicatorName && <span className="error">{errors.indicatorName}</span>}

                                                <div className="target-row">
                                                    <div className="color-picker">
                                                        <label>Color</label>
                                                        <input
                                                            type="color"
                                                            className="color-input"
                                                            value={color}
                                                            onChange={(e) => setColor(e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="daily-target">
                                                        <label>Daily Norm</label>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            placeholder="000"
                                                            className="daily-input"
                                                            value={dailyNorm}
                                                            onChange={(e) => setDailyNorm(e.target.value)}
                                                        />
                                                        {errors.dailyNorm && <span className="error">{errors.dailyNorm}</span>}
                                                    </div>

                                                    <div className="monthly-target">
                                                        <label>Monthly Target</label>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            placeholder="000"
                                                            className="monthly-input"
                                                            value={indicatorTarget}
                                                            onChange={(e) => setIndicatorTarget(e.target.value)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    e.preventDefault();
                                                                    handleAddOrUpdate();
                                                                }
                                                            }}
                                                        />
                                                        {errors.indicatorTarget && <span className="error">{errors.indicatorTarget}</span>}
                                                    </div>
                                                </div>

                                                <div className="target-actions">
                                                    <button className="target-delete-btn" onClick={handleCancel}>Discard</button>
                                                    <button
                                                        className={`target-add-btn ${indicatorName && indicatorTarget ? "active" : ""}`}
                                                        onClick={handleAddOrUpdate}
                                                        disabled={!indicatorName || !indicatorTarget || loading}
                                                    >
                                                        {loading ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : "Save"}
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div key={indicator.id} className="target-card-lead-indicator">
                                                <div className="target-header" onMouseEnter={(e) => handleMouseEnter(e, indicator.name)}
                                                    onMouseLeave={handleMouseLeave}>
                                                    <span title={indicator.name}>{indicator.name}</span>
                                                    <div className="color-circle" style={{ backgroundColor: indicator.color }}></div>
                                                </div>
                                                <div className="lead-body">
                                                    <div className="daily-label">
                                                        <span>Daily Norm</span>
                                                        <strong>{indicator.dailyNorm}</strong>
                                                    </div>
                                                    <div className="lead-monthly-label">
                                                        <span>Monthly Target</span>
                                                        <strong>{indicator.target}</strong>
                                                    </div>
                                                </div>
                                                <div className="target-actions">
                                                    <img className="target-funnel-edit" src={Edit} onClick={() => handleEdit(index)} alt="Edit" />
                                                    <img className="target-funnel-delete" src={Delete} onClick={() => handleDelete(index)} alt="Delete" />
                                                </div>
                                            </div>
                                        )
                                    )}

                                    {isAddingNewIndicator && editingIndex === null && (
                                        <div className="target-box">
                                            <input
                                                type="text"
                                                placeholder="Lead Indicator Name"
                                                className="target-input"
                                                value={indicatorName}
                                                onChange={(e) => setIndicatorName(e.target.value)}
                                            />
                                            {errors.indicatorName && <span className="error">{errors.indicatorName}</span>}

                                            <div className="target-row">
                                                <div className="color-picker">
                                                    <label>Color</label>
                                                    <input
                                                        type="color"
                                                        className="color-input"
                                                        value={color}
                                                        onChange={(e) => setColor(e.target.value)}
                                                    />
                                                </div>
                                                <div className="daily-target">
                                                    <label>Daily Norm</label>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        placeholder="000"
                                                        className="daily-input"
                                                        value={dailyNorm}
                                                        onChange={(e) => setDailyNorm(e.target.value)}
                                                    />
                                                    {errors.dailyNorm && <span className="error">{errors.dailyNorm}</span>}
                                                </div>

                                                <div className="monthly-target">
                                                    <label>Monthly Target</label>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        placeholder="000"
                                                        className="monthly-input"
                                                        value={indicatorTarget}
                                                        onChange={(e) => setIndicatorTarget(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                                handleAddOrUpdate();
                                                            }
                                                        }}
                                                    />
                                                    {errors.indicatorTarget && <span className="error">{errors.indicatorTarget}</span>}
                                                </div>
                                            </div>

                                            <div className="target-actions">
                                                <button className="target-delete-btn" onClick={handleCancel}>Discard</button>
                                                <button
                                                    className={`target-add-btn-add-button ${indicatorName && indicatorTarget ? "active" : ""}`}
                                                    onClick={handleAddOrUpdate}
                                                    disabled={!indicatorName || !indicatorTarget || loading}
                                                >
                                                    {loading ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : "Add"}
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {!isAddingNewIndicator && editingIndex === null && (
                                        // {!isAddingNewIndicator && editingIndex === null && (
                                        <button
                                            className={indicators.length === 0 ? "new-lead-indicator-btn" : "add-indicator-btn"}
                                            onClick={() => setIsAddingNewIndicator(true)}
                                        >
                                            {indicators.length === 0 ? "New Lead Indicator" : "Add"}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    <p style={{ marginLeft: "2%", fontSize: "12px", color: "#888", fontFamily: "Inter" }}>
                        The items displayed from left to right are arranged vertically from top to bottom in the app.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default LeadIndicatorConfiguration;